import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { TicketIcon, ChatBubbleLeftRightIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { supportAPI, getErrorMessage } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const CATEGORIES = ["general", "order", "payment", "return", "product", "delivery"];

export default function SupportPage() {
  const { isAuthenticated, user } = useAuth();
  const [activeTab, setActiveTab] = useState<"tickets" | "contact">("tickets");
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketForm, setTicketForm] = useState({
    subject: "", description: "", category: "general", order_number: "",
    guest_name: "", guest_email: "",
  });
  const [contactForm, setContactForm] = useState({ name: user?.full_name || "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadTickets();
    }
  }, [isAuthenticated]);

  const loadTickets = async () => {
    try {
      const res = await supportAPI.myTickets();
      setTickets(res.data);
    } catch { }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setTicketLoading(true);
    try {
      const data: any = {
        subject: ticketForm.subject,
        description: ticketForm.description,
        category: ticketForm.category,
      };
      if (ticketForm.order_number) data.order_number = ticketForm.order_number;
      if (!isAuthenticated) {
        data.guest_name = ticketForm.guest_name;
        data.guest_email = ticketForm.guest_email;
      }
      const res = await supportAPI.createTicket(data);
      toast.success(`Ticket #${res.data.ticket_number} created!`);
      setTicketForm({ subject: "", description: "", category: "general", order_number: "", guest_name: "", guest_email: "" });
      if (isAuthenticated) loadTickets();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTicketLoading(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await supportAPI.contactForm(contactForm);
      toast.success("Message sent! We'll reply soon.");
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const statusColors: Record<string, string> = {
    open: "bg-yellow-100 text-yellow-800",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-100 text-gray-600",
  };

  return (
    <>
      <Helmet><title>Support – Kushi Fashions</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Center</h1>
          <p className="text-gray-500">We're here to help. Create a ticket or send us a message.</p>
        </div>

        {/* Options cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: TicketIcon, title: "Create Ticket", desc: "Get help with orders, payments, returns", action: () => setActiveTab("tickets") },
            { icon: ChatBubbleLeftRightIcon, title: "Live Chat", desc: "Chat with our AI assistant", action: () => {} },
            { icon: EnvelopeIcon, title: "Contact Form", desc: "Send a general enquiry", action: () => setActiveTab("contact") },
          ].map((opt) => (
            <button
              key={opt.title}
              onClick={opt.action}
              className="card p-5 text-center hover:shadow-md transition-shadow group"
            >
              <opt.icon className="h-10 w-10 text-primary-500 mx-auto mb-3 group-hover:text-primary-600" />
              <h3 className="font-semibold text-gray-800">{opt.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{opt.desc}</p>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {(["tickets", "contact"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "tickets" ? "Support Tickets" : "Contact Form"}
            </button>
          ))}
        </div>

        {activeTab === "tickets" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Create ticket */}
            <div className="card p-5">
              <h2 className="font-bold text-gray-900 mb-4">Create Support Ticket</h2>
              <form onSubmit={handleCreateTicket} className="space-y-4">
                {!isAuthenticated && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                      <input type="text" value={ticketForm.guest_name} onChange={(e) => setTicketForm(f => ({ ...f, guest_name: e.target.value }))} required className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                      <input type="email" value={ticketForm.guest_email} onChange={(e) => setTicketForm(f => ({ ...f, guest_email: e.target.value }))} required className="input-field" />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={ticketForm.category} onChange={(e) => setTicketForm(f => ({ ...f, category: e.target.value }))} className="input-field">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input type="text" value={ticketForm.subject} onChange={(e) => setTicketForm(f => ({ ...f, subject: e.target.value }))} required className="input-field" placeholder="Brief description of your issue" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order ID (optional)</label>
                  <input type="text" value={ticketForm.order_number} onChange={(e) => setTicketForm(f => ({ ...f, order_number: e.target.value }))} className="input-field" placeholder="KF-20240101-XXXXX" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm(f => ({ ...f, description: e.target.value }))}
                    required rows={4}
                    className="input-field resize-none"
                    placeholder="Describe your issue in detail..."
                  />
                </div>
                <button type="submit" disabled={ticketLoading} className="btn-primary w-full py-2.5">
                  {ticketLoading ? "Submitting..." : "Submit Ticket"}
                </button>
              </form>
            </div>

            {/* My tickets */}
            <div>
              <h2 className="font-bold text-gray-900 mb-4">
                {isAuthenticated ? "My Tickets" : "Common Questions"}
              </h2>
              {isAuthenticated ? (
                tickets.length === 0 ? (
                  <div className="card p-8 text-center text-gray-400">
                    <TicketIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No tickets yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tickets.map((ticket) => (
                      <Link key={ticket.id} to={`/support/tickets/${ticket.id}`} className="card p-4 block hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 line-clamp-1">{ticket.subject}</p>
                            <p className="text-xs text-gray-400 mt-1">#{ticket.ticket_number} · {ticket.category}</p>
                          </div>
                          <span className={`badge flex-shrink-0 ${statusColors[ticket.status] || "bg-gray-100 text-gray-600"}`}>
                            {ticket.status.replace("_", " ")}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {[
                    { q: "What is the return policy?", a: "7 days from delivery." },
                    { q: "How long does delivery take?", a: "5-7 business days." },
                    { q: "How do I track my order?", a: "Go to My Orders after login." },
                  ].map((faq) => (
                    <div key={faq.q} className="card p-4">
                      <p className="font-medium text-gray-800 text-sm">{faq.q}</p>
                      <p className="text-gray-500 text-sm mt-1">{faq.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "contact" && (
          <div className="max-w-lg mx-auto card p-6">
            <h2 className="font-bold text-gray-900 mb-4">Contact Us</h2>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                <input type="text" value={contactForm.name} onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))} required className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={contactForm.email} onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))} required className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input type="text" value={contactForm.subject} onChange={(e) => setContactForm(f => ({ ...f, subject: e.target.value }))} required className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea value={contactForm.message} onChange={(e) => setContactForm(f => ({ ...f, message: e.target.value }))} required rows={5} className="input-field resize-none" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
